/**
 * Bulgudan ilgili ekrana gitme (deep-link) mekanizması.
 *
 * Bir bulguya tıklandığında yalnızca sayfayı açmak yetmiyor — "Pasif listing —
 * Shopify" tıklanınca Pazaryeri Listeleri'nin Shopify'a filtrelenmiş hâli açılmalı.
 * Sayfalar arası state taşımak için tek seferlik bir "odak" kaydı bırakılıyor;
 * hedef sayfa mount olurken bunu tüketip filtresine uyguluyor.
 *
 * sessionStorage kullanılıyor ki sayfa yenilense de kaybolmasın, ama tek seferlik
 * okunduğu için geri dönüldüğünde filtre tekrar uygulanmıyor.
 */

const KEY = 'poyais_pixa_focus';

export type Focus = {
  nav: string;
  tab?: string;          // sayfa içi sekme (Kayıtlar, Fiyatlandırma, Asistan)
  platform?: string;     // pazaryeri adı
  status?: string;       // listing durumu (notMatch)
  problematic?: boolean; // sorunlu kargo
  image?: string;        // katalog görsel filtresi
  kind?: string;
  scope?: string;
};

/** Bulgu tipinden hedef ekran ve filtre. scope çoğu bulguda pazaryeri adı. */
export function findingTarget(kind: string, scope?: string): Focus | null {
  const platform = (scope || '').split('·')[0].trim() || undefined;
  switch (kind) {
    case 'low_stock':
      return { nav: 'pixa-inventory', kind, scope };
    case 'pending_questions':
      return { nav: 'pixa-asistan', tab: 'questions', kind, scope };
    case 'problematic_shipment':
      return { nav: 'pixa-shipments', problematic: true, kind, scope };
    case 'transfer_failed':
      return { nav: 'pixa-integrations', platform, kind, scope };
    case 'integration_error':
      return { nav: 'pixa-integrations', kind, scope };
    case 'integration_down':
      return { nav: 'pixa-integrations', platform, kind, scope };
    case 'listing_not_matched':
      return { nav: 'pixa-listings', platform, status: 'notMatch', kind, scope };
    case 'listing_closed':
    case 'listing_inactive':
    case 'listing_disconnected':
    case 'listing_locked':
    case 'not_listed':
    case 'price_below_cost':
    case 'price_deviation':
      return { nav: 'pixa-listings', platform, kind, scope };
    default:
      return null;
  }
}

/** Odak bırak ve hedefe git. */
export function goToFinding(kind: string, scope?: string): boolean {
  const target = findingTarget(kind, scope);
  if (!target) return false;
  try { sessionStorage.setItem(KEY, JSON.stringify(target)); } catch { }
  window.dispatchEvent(new CustomEvent('poyais-nav', { detail: target.nav }));
  return true;
}

/** Hedef sayfa mount olurken çağırır: kendisine bırakılmış odağı tüketir. */
export function takeFocus(nav: string): Focus | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const f = JSON.parse(raw) as Focus;
    if (f?.nav !== nav) return null;
    sessionStorage.removeItem(KEY);
    return f;
  } catch {
    return null;
  }
}


/**
 * Hata logu satırından ilgili ekrana yönlendirme.
 *
 * Pixa hata loglarında yapısal bir alan yok, yalnızca serbest metin bir `comment`
 * var ("Hepsiburada eşleşmeyen ürünler", "Nebim sipariş aktarımı"). Metinden
 * pazaryeri adı ve sorun tipi çıkarılıp uygun ekrana filtreli gidiliyor.
 */
export function errorLogTarget(comment: string, platforms: string[] = []): Focus | null {
  const text = (comment || '').toLocaleLowerCase('tr');
  if (!text) return null;
  const platform = platforms.find(p => text.includes(p.toLocaleLowerCase('tr')));

  if (text.includes('eşleşmeyen')) {
    return { nav: 'pixa-listings', platform, status: 'notMatch' };
  }
  if (text.includes('sipariş')) {
    return { nav: 'pixa-orders', platform };
  }
  if (text.includes('görsel')) {
    return { nav: 'pixa-products', image: 'none' };
  }
  if (text.includes('stok')) {
    return { nav: 'pixa-inventory' };
  }
  if (platform) {
    return { nav: 'pixa-listings', platform };
  }
  return null;
}

export function goToErrorLog(comment: string, platforms: string[] = []): boolean {
  const target = errorLogTarget(comment, platforms);
  if (!target) return false;
  try { sessionStorage.setItem(KEY, JSON.stringify(target)); } catch { }
  window.dispatchEvent(new CustomEvent('poyais-nav', { detail: target.nav }));
  return true;
}
